// @givmo/board — a link set in a narrow box, breaking between its parts (CBG-FIX14).
//
// A director's access link is one long unbroken run of characters, and the box it is shown in is
// narrower than it: at 390 the old break-anywhere rule split it inside a word (`…director/r` /
// `edeem?…`) and, at 1440, left one character of the token alone on the last line. So the link gets a
// line-break opportunity at each of its parts — after the scheme's `//` and each `/` of the path, after
// the `?`, between a parameter's name and its value, and before each `&` — and the box's
// `wrap-anywhere` breaks inside a part (the long token) only when the part alone is wider than the box.
//
// A `<wbr>` carries no text, so the link reads, selects and copies exactly as built: the text on screen
// is the officer's fallback when the clipboard is refused. (The consent panel's `breakableAtDots`
// shape, `apps/consent/app/components/consent-panel.tsx`.)

import type { ReactNode } from 'react';

/** The link as text runs with a `<wbr>` at each of its parts. Pure: link in, nodes out. */
export function breakableLink(link: string): ReactNode[] {
  const schemeEnd = link.indexOf('://');
  const pathStart = schemeEnd === -1 ? 0 : schemeEnd + 3;
  const queryStart = link.indexOf('?');
  const inQuery = (index: number) => queryStart !== -1 && index > queryStart;
  const nodes: ReactNode[] = [];
  let part = '';
  const closePart = () => {
    if (part.length === 0) return;
    nodes.push(part, <wbr key={nodes.length} />);
    part = '';
  };
  for (let index = 0; index < link.length; index += 1) {
    const character = link.charAt(index);
    // A parameter starts its own line: the break falls BEFORE its `&`.
    if (character === '&' && inQuery(index)) closePart();
    part += character;
    // The scheme's second slash, then every `/` of the path; the `?`; a parameter's `=`.
    const pathSlash = character === '/' && index >= pathStart - 1 && !inQuery(index);
    if (pathSlash || index === queryStart || (character === '=' && inQuery(index))) closePart();
  }
  if (part.length > 0) nodes.push(part);
  return nodes;
}
